/**
 * Page Annuaire Artisans
 * - Liste des artisans / entreprises de travaux avec recherche
 * - CRUD basique : ajouter, modifier, supprimer
 * - Bouton "Envoyer un email" (relance, demande de devis...)
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRADES = [
  'Maconnerie', 'Plomberie', 'Electricite', 'Couverture', 'Charpente',
  'Menuiserie', 'Peinture', 'Carrelage', 'Cuisine', 'Sanitaires',
  'Isolation', 'Chauffage / PAC', 'VMC', 'Electromenager', 'Architecte',
  'Bureau d\'etudes', 'Diagnostic / DPE', 'Syndic / Gestion', 'Autre'
];

const ContractorsPage = () => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [trade, setTrade] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [edit, setEdit] = useState(null);
  const [emailFor, setEmailFor] = useState(null);
  const [missionFor, setMissionFor] = useState(null);
  const [missionsView, setMissionsView] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (trade) params.set('trade', trade);
      const r = await axios.get(`${API}/contractors${params.toString() ? `?${params}` : ''}`);
      setItems(r.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [search, trade]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const remove = async (c) => {
    if (!window.confirm(`Supprimer ${c.company_name} ?`)) return;
    try {
      await axios.delete(`${API}/contractors/${c.contractor_id}`);
      fetchItems();
    } catch (e) { alert(decodeApiError(e)); }
  };

  return (
    <div className="p-4 lg:p-8" data-testid="contractors-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Annuaire Artisans</h1>
          <p className="text-gray-600 mt-1">Vos entreprises de travaux + envoi d'emails (relance, devis).</p>
        </div>
        <button onClick={() => { setEdit(null); setShowModal(true); }}
                className="realease-btn-primary flex items-center gap-2"
                data-testid="new-contractor-btn">
          <span className="text-lg leading-none">+</span> Nouvel artisan
        </button>
      </div>

      <div className="realease-card p-4 mb-4 flex flex-wrap gap-3">
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
               placeholder="Rechercher (nom, contact, email, ville...)"
               className="realease-input flex-1 min-w-[240px]"
               data-testid="contractors-search" />
        <select value={trade} onChange={e => setTrade(e.target.value)}
                className="realease-input w-56" data-testid="contractors-filter-trade">
          <option value="">Tous metiers</option>
          {TRADES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      <div className="realease-card overflow-hidden">
        {loading ? (
          <p className="p-8 text-center text-gray-400 italic">Chargement...</p>
        ) : items.length === 0 ? (
          <p className="p-8 text-center text-gray-400 italic">Aucun artisan. Ajoutez-en un avec le bouton ci-dessus.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left">
                <th className="p-3 font-semibold">Entreprise</th>
                <th className="p-3 font-semibold">Metier</th>
                <th className="p-3 font-semibold">Contact</th>
                <th className="p-3 font-semibold">Telephone / Email</th>
                <th className="p-3 font-semibold">Ville</th>
                <th className="p-3 font-semibold">Note</th>
                <th className="p-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(c => (
                <tr key={c.contractor_id} className="border-t border-gray-100" data-testid={`contractor-${c.contractor_id}`}>
                  <td className="p-3 font-medium text-gray-900">{c.company_name}</td>
                  <td className="p-3 text-gray-600">{c.trade || '-'}</td>
                  <td className="p-3 text-gray-700">{c.contact_name || '-'}</td>
                  <td className="p-3 text-xs">
                    {c.phone && <div className="font-mono">{c.phone}</div>}
                    {c.email && <div className="font-mono text-[#008A8D]">{c.email}</div>}
                  </td>
                  <td className="p-3 text-gray-600">{c.city || '-'}</td>
                  <td className="p-3">{c.rating ? '★'.repeat(c.rating) : '-'}</td>
                  <td className="p-3">
                    <div className="flex gap-3 flex-wrap">
                      {c.email && (
                        <button onClick={() => setEmailFor(c)}
                                className="text-xs font-semibold text-[#008A8D] hover:underline"
                                data-testid={`email-${c.contractor_id}`}>
                          Email
                        </button>
                      )}
                      <button onClick={() => setMissionFor(c)}
                              className="text-xs font-semibold text-[#A57F4F] hover:underline"
                              data-testid={`mission-${c.contractor_id}`}>
                        + Mission
                      </button>
                      <button onClick={() => setMissionsView(c)}
                              className="text-xs font-semibold text-gray-600 hover:underline"
                              data-testid={`missions-view-${c.contractor_id}`}>
                        Missions
                      </button>
                      <button onClick={() => { setEdit(c); setShowModal(true); }}
                              className="text-xs font-semibold text-gray-600 hover:underline">
                        Modifier
                      </button>
                      <button onClick={() => remove(c)}
                              className="text-xs font-semibold text-red-500 hover:underline">
                        Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <ContractorModal contractor={edit}
                         onClose={() => { setShowModal(false); setEdit(null); }}
                         onSuccess={() => { setShowModal(false); setEdit(null); fetchItems(); }} />
      )}
      {emailFor && (
        <EmailContractorModal contractor={emailFor}
                              onClose={() => setEmailFor(null)}
                              onSent={() => { setEmailFor(null); fetchItems(); }} />
      )}
      {missionFor && (
        <ConvertMissionModal contractor={missionFor}
                              onClose={() => setMissionFor(null)}
                              onSuccess={() => { setMissionFor(null); fetchItems(); }} />
      )}
      {missionsView && (
        <MissionsListModal contractor={missionsView}
                              onClose={() => setMissionsView(null)} />
      )}
    </div>
  );
};

// Iter 111b : helper partage pour decoder les erreurs FastAPI/Pydantic.
// FastAPI 422 retourne detail=Array[{loc, msg, ...}] qui ne s'affiche pas comme
// string -> bandeau "Erreur" generique inutile. Ce helper extrait un message lisible.
const decodeApiError = (err, fallback = 'Erreur') => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d.map((e) => {
      const field = Array.isArray(e?.loc) ? e.loc[e.loc.length - 1] : '';
      return `${field ? field + ' : ' : ''}${e?.msg || ''}`;
    }).join(' | ') || 'Erreur de validation';
  }
  if (err?.message) return `Erreur reseau : ${err.message}`;
  return fallback;
};

const ContractorModal = ({ contractor, onClose, onSuccess }) => {
  const isEdit = !!contractor;
  const [form, setForm] = useState({
    company_name: contractor?.company_name || '',
    contact_name: contractor?.contact_name || '',
    trade: contractor?.trade || '',
    email: contractor?.email || '',
    phone: contractor?.phone || '',
    address: contractor?.address || '',
    city: contractor?.city || '',
    postal_code: contractor?.postal_code || '',
    siret: contractor?.siret || '',
    rating: contractor?.rating || '',
    notes: contractor?.notes || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      const payload = { ...form };
      if (payload.email === '') delete payload.email;
      if (payload.rating === '' || payload.rating === null) delete payload.rating;
      else payload.rating = parseInt(payload.rating, 10);
      if (isEdit) {
        await axios.put(`${API}/contractors/${contractor.contractor_id}`, payload);
      } else {
        await axios.post(`${API}/contractors`, payload);
      }
      onSuccess();
    } catch (err) {
      setError(decodeApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-auto" data-testid="contractor-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">{isEdit ? 'Modifier l\'artisan' : 'Nouvel artisan'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        {error && <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs font-semibold text-gray-600">Entreprise *</label>
              <input type="text" required value={form.company_name}
                     onChange={e => set('company_name', e.target.value)}
                     className="realease-input" data-testid="contractor-company" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Metier</label>
              <select value={form.trade} onChange={e => set('trade', e.target.value)} className="realease-input">
                <option value="">--</option>
                {TRADES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Contact</label>
              <input type="text" value={form.contact_name}
                     onChange={e => set('contact_name', e.target.value)} className="realease-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Email</label>
              <input type="email" value={form.email} onChange={e => set('email', e.target.value)}
                     className="realease-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Telephone</label>
              <input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)}
                     className="realease-input" />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-semibold text-gray-600">Adresse</label>
              <input type="text" value={form.address} onChange={e => set('address', e.target.value)}
                     className="realease-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Ville</label>
              <input type="text" value={form.city} onChange={e => set('city', e.target.value)}
                     className="realease-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Code postal</label>
              <input type="text" value={form.postal_code} onChange={e => set('postal_code', e.target.value)}
                     className="realease-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">SIRET</label>
              <input type="text" value={form.siret} onChange={e => set('siret', e.target.value)}
                     className="realease-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Note (1-5)</label>
              <input type="number" min="1" max="5" value={form.rating}
                     onChange={e => set('rating', e.target.value)} className="realease-input" />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-semibold text-gray-600">Notes</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
                        className="realease-input h-20" />
            </div>
          </div>
          <div className="flex gap-3 pt-3">
            <button type="button" onClick={onClose}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50">
              Annuler
            </button>
            <button type="submit" disabled={loading}
                    className="flex-1 realease-btn-primary disabled:opacity-50"
                    data-testid="contractor-save">
              {loading ? 'Enregistrement...' : isEdit ? 'Enregistrer' : 'Creer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EmailContractorModal = ({ contractor, onClose, onSent }) => {
  const [subject, setSubject] = useState(`Demande / Relance - ${contractor.company_name}`);
  const [body, setBody] = useState(
    `Bonjour ${contractor.contact_name || ''},\n\n[Votre message ici]\n\nCordialement,\nL'equipe RealEase`
  );
  const [projectId, setProjectId] = useState('');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/projects`);
        setProjects(r.data || []);
      } catch {}
    })();
  }, []);

  const send = async () => {
    if (!subject.trim() || !body.trim()) {
      setError('Sujet et message requis'); return;
    }
    setLoading(true); setError('');
    try {
      await axios.post(`${API}/contractors/${contractor.contractor_id}/email`, {
        subject: subject.trim(),
        body,
        project_id: projectId || null,
      });
      onSent();
    } catch (e) {
      setError(decodeApiError(e, 'Erreur lors de l\'envoi'));
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-xl mx-4 max-h-[90vh] overflow-auto" data-testid="contractor-email-modal">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-bold">Email a {contractor.company_name}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        <p className="text-xs text-gray-500 mb-3">Destinataire : <span className="font-mono">{contractor.email}</span></p>
        {error && <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-600">Sujet</label>
            <input type="text" value={subject} onChange={e => setSubject(e.target.value)}
                   className="realease-input" data-testid="contractor-email-subject" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Projet associe (optionnel)</label>
            <select value={projectId} onChange={e => setProjectId(e.target.value)} className="realease-input">
              <option value="">Aucun</option>
              {projects.filter(p => p.status !== 'termine').map(p => (
                <option key={p.project_id} value={p.project_id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Message</label>
            <textarea value={body} onChange={e => setBody(e.target.value)}
                      className="realease-input h-48 font-sans"
                      data-testid="contractor-email-body" />
          </div>
        </div>
        <div className="flex gap-3 pt-4">
          <button onClick={onClose}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50">
            Annuler
          </button>
          <button onClick={send} disabled={loading}
                  className="flex-1 realease-btn-primary disabled:opacity-50"
                  data-testid="contractor-email-send">
            {loading ? 'Envoi...' : 'Envoyer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContractorsPage;

// ============ ConvertMissionModal : convertit l'artisan + projet en mission travaux ============

const ConvertMissionModal = ({ contractor, onClose, onSuccess }) => {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [description, setDescription] = useState(
    contractor.trade ? `${contractor.trade} - ${contractor.company_name}` : `Mission - ${contractor.company_name}`
  );
  const [targetDate, setTargetDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 14);
    return d.toISOString().slice(0, 10);
  });
  const [quoteStatus, setQuoteStatus] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/projects`);
        setProjects((r.data || []).filter(p => p.status !== 'termine'));
      } catch {}
    })();
  }, []);

  const submit = async () => {
    if (!projectId) { setError('Selectionnez un projet'); return; }
    if (!description.trim()) { setError('Description requise'); return; }
    setLoading(true); setError('');
    try {
      await axios.post(`${API}/contractors/${contractor.contractor_id}/convert-mission`, {
        project_id: projectId,
        description: description.trim(),
        target_date: targetDate,
        quote_status: quoteStatus,
      });
      onSuccess();
    } catch (e) {
      setError(decodeApiError(e));
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-lg mx-4" data-testid="convert-mission-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Nouvelle mission travaux</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        <p className="text-xs text-gray-500 mb-4">Cree un delai dans le projet selectionne, lie a l'artisan <b>{contractor.company_name}</b>. Toutes les coordonnees (email, telephone) seront copiees pour la relance auto.</p>
        {error && <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-600">Projet *</label>
            <select value={projectId} onChange={e => setProjectId(e.target.value)} className="realease-input" data-testid="mission-project">
              <option value="">-- Selectionnez --</option>
              {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name} ({p.client_name || '-'})</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Description / objet de la mission</label>
            <input type="text" value={description} onChange={e => setDescription(e.target.value)}
                   className="realease-input" data-testid="mission-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600">Echeance</label>
              <input type="date" value={targetDate} onChange={e => setTargetDate(e.target.value)}
                     className="realease-input" data-testid="mission-target-date" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Statut devis</label>
              <select value={quoteStatus} onChange={e => setQuoteStatus(e.target.value)} className="realease-input">
                <option value="pending">En attente devis</option>
                <option value="sent">Devis envoye</option>
                <option value="accepted">Devis accepte</option>
                <option value="refused">Devis refuse</option>
              </select>
            </div>
          </div>
        </div>
        <div className="flex gap-3 pt-4">
          <button onClick={onClose}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50">
            Annuler
          </button>
          <button onClick={submit} disabled={loading}
                  className="flex-1 realease-btn-primary disabled:opacity-50"
                  data-testid="mission-create">
            {loading ? 'Creation...' : 'Creer la mission'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ MissionsListModal : voir les missions d'un artisan + changer le statut devis ============

const QUOTE_STATUS_LABELS = {
  pending: { label: 'En attente devis', color: 'bg-gray-100 text-gray-700' },
  sent: { label: 'Devis envoye', color: 'bg-blue-100 text-blue-700' },
  accepted: { label: 'Devis accepte', color: 'bg-emerald-100 text-emerald-700' },
  refused: { label: 'Devis refuse', color: 'bg-red-100 text-red-700' },
};

const MissionsListModal = ({ contractor, onClose }) => {
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMissions = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/contractors/${contractor.contractor_id}/missions`);
      setMissions(r.data || []);
    } catch {} finally { setLoading(false); }
  }, [contractor.contractor_id]);

  useEffect(() => { fetchMissions(); }, [fetchMissions]);

  const setQuoteStatus = async (m, status) => {
    try {
      await axios.patch(`${API}/contractors/missions/${m.delay_id}/quote-status`, { quote_status: status });
      fetchMissions();
    } catch (e) { alert(decodeApiError(e)); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-3xl mx-4 max-h-[90vh] overflow-auto" data-testid="missions-list-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold">Missions de {contractor.company_name}</h2>
            <p className="text-xs text-gray-500">{contractor.trade || '-'}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        {loading ? (
          <p className="text-gray-400 italic">Chargement...</p>
        ) : missions.length === 0 ? (
          <p className="text-gray-400 italic">Aucune mission pour cet artisan.</p>
        ) : (
          <div className="space-y-2">
            {missions.map(m => {
              const qs = QUOTE_STATUS_LABELS[m.quote_status] || QUOTE_STATUS_LABELS.pending;
              return (
                <div key={m.delay_id} className="border border-gray-200 rounded-lg p-3" data-testid={`mission-row-${m.delay_id}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{m.description}</p>
                      <p className="text-xs text-gray-500">
                        Projet : <a href={`/projects/${m.project_id}`} className="text-[#008A8D] hover:underline">{m.project_name || m.project_id}</a>
                        {' · '}Echeance : <b>{m.target_date}</b>
                      </p>
                    </div>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${qs.color}`}>{qs.label}</span>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {Object.entries(QUOTE_STATUS_LABELS).map(([k, v]) => (
                      <button key={k} onClick={() => setQuoteStatus(m, k)}
                              disabled={m.quote_status === k}
                              className={`text-[11px] px-2 py-0.5 rounded-full font-semibold border ${m.quote_status === k ? v.color + ' border-transparent cursor-default' : 'bg-white text-gray-600 border-gray-300 hover:border-[#008A8D] hover:text-[#008A8D]'}`}
                              data-testid={`mission-${m.delay_id}-set-${k}`}>
                        → {v.label}
                      </button>
                    ))}
                    <a href={`/projects/${m.project_id}?tab=delais`}
                       className="text-[11px] px-2 py-0.5 rounded-full font-semibold border border-gray-300 text-gray-600 hover:border-[#008A8D] hover:text-[#008A8D]">
                      Voir delai →
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
